// Copyright ©️ 2025 eVolve MEP, LLC

import { useState } from 'react';

import type { FolderData } from '@aps_sdk/data-management';
import { Flex, Popover, Stack, Text } from '@mantine/core';

type Props = {
  folderPath: FolderData[];
  clearFolderPath: () => void;
  onFolderSelected: (folder: FolderData) => void;
};

export const AutodeskFolderBreadcrumb = ({ folderPath, onFolderSelected, clearFolderPath }: Props) => {
  const [opened, setOpened] = useState(false);
  return (
    <Flex gap="xs" style={{ textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {folderPath.length > 0 && (
        <>
          <Text style={{ userSelect: 'none' }} c="dimmed" span>
            /
          </Text>
          <Popover opened={opened} onChange={setOpened} shadow="xs" withArrow position="bottom-start">
            <Popover.Target>
              <Text onClick={() => setOpened(true)} className="link" c="black" span fw={600}>
                ...
              </Text>
            </Popover.Target>
            <Popover.Dropdown miw={200} maw={450}>
              <Stack gap={3}>
                <Flex gap="xs">
                  <Text style={{ userSelect: 'none' }} c="dimmed" span>
                    /
                  </Text>
                  <Text
                    span
                    className="link"
                    onClick={() => {
                      setOpened(false);
                      clearFolderPath();
                    }}
                    c="gray.7"
                    title="root"
                  >
                    root
                  </Text>
                </Flex>
                {folderPath
                  .slice(1)
                  .reverse()
                  .map((f, i) => (
                    <Flex gap="xs" key={f.id} ml={(i + 1) * 12}>
                      <Text style={{ userSelect: 'none' }} c="dimmed" span>
                        /
                      </Text>
                      <Text
                        span
                        className="link"
                        onClick={() => {
                          setOpened(false);
                          onFolderSelected(f);
                        }}
                        c="black"
                        style={{
                          textOverflow: 'ellipsis',
                        }}
                        title={f.attributes.displayName}
                        lineClamp={1}
                      >
                        {f.attributes.displayName}
                      </Text>
                    </Flex>
                  ))}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </>
      )}
      <Text style={{ userSelect: 'none' }} c="dimmed">
        /
      </Text>
      <Text
        style={{ userSelect: 'none', textOverflow: 'ellipsis' }}
        lineClamp={1}
        title={folderPath[0]?.attributes.displayName}
      >
        {folderPath[0]?.attributes.displayName}
      </Text>
    </Flex>
  );
};
